import {controller} from '@github/catalyst'
import {generateAppId, registerAppId} from '@github-ui/app-uuid'
import {create} from '@github-ui/dotcom-schema/protobuf'
import {SafeHtmlSchema} from '@github-ui/dotcom-schema/types/common/v1/safe_html'
import {currentState, updateCurrentState} from '@github-ui/history'
import {getQueryClient} from '@github-ui/query-client'
import {
  type AnyTanStackRouter,
  createBrowserRouter,
  type RouteObject,
  RouterProvider,
  type RouterProviderProps,
  TanStackRouterProvider,
} from '@github-ui/react-router'
import type {QueryClient} from '@tanstack/react-query'

import type {NavigatorRouteRegistration} from './app-routing-types'
import {createBrowserHistory, type GitHubBrowserHistory} from './create-browser-history'
import type {EmbeddedData} from './embedded-data-types'
import {wrapRouterNavigateForTurboAndSoftNavs} from './future/apply-router-navigate-override'
import {getReactDataRouterApp, isTanStackRouterEnabled} from './future/data-router-app-registry'
import {routesWithProviders, tanStackRouterWithProviders} from './future/RoutesWithProviders'
import {wrapWithAppShell} from './future/wrap-with-app-shell'
import {getReactNavigatorApp, type NavigatorAppRegistration} from './navigator-app-registry'
import {NavigatorClientEntry} from './NavigatorClientEntry'
import {preloadMatchedDataRouterComponents, preloadMatchedRouteComponents} from './preload-matched-route-components'
import {AppProfiler, ProfilerProvider} from './ProfilerContext'
import {ReactBaseElement, type ReactComponentPreloadHandle} from './ReactBaseElement'

declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'react-app': React.DetailedHTMLProps<React.HTMLAttributes<ReactAppElement>, ReactAppElement>
    }
  }
}

export type RouterContext = {queryClient: QueryClient; embeddedData: EmbeddedData}
export type RouterOrHistory = RouterProviderProps['router'] | GitHubBrowserHistory | AnyTanStackRouter
type PreparedDataRouter = {routes: RouteObject[]; withTanStackRouter: boolean}

/**
 * Registrations, routes and preloads prepared for a single connection. A custom element can be
 * disconnected and reconnected, so this state is recreated on connect to avoid reusing routes built
 * from a previous embedded payload, or a preload selected for a previous path or router mode.
 * Work started by an earlier connection keeps a reference to its own state object, so it can never
 * overwrite the current connection's state.
 */
type PreparedConnectionState = {
  componentPreloadStarted?: boolean
  navigatorRegistration?: Promise<NavigatorAppRegistration>
  matchedNavigatorComponents?: Promise<void>
  preparedDataRouter?: Promise<PreparedDataRouter>
  matchedDataRouterComponents?: Promise<void>
}

async function clearPromiseOnRejection<T>(promise: Promise<T>, clear: (promise: Promise<T>) => void) {
  try {
    await promise
  } catch {
    clear(promise)
  }
}

function clearCachedPromiseOnRejection<T>(promise: Promise<T>, clear: (promise: Promise<T>) => void) {
  void clearPromiseOnRejection(promise, clear)
  return promise
}

// TanStack Router instances have history.destroy(); React Router instances have dispose()
export function isTanStackRouter(routerOrHistory: RouterOrHistory): routerOrHistory is AnyTanStackRouter {
  const history = 'history' in routerOrHistory ? routerOrHistory.history : null
  return typeof history?.destroy === 'function' && !('dispose' in routerOrHistory)
}

// Copied from React Router
const createKey = () => Math.random().toString(36).substr(2, 8)

// What is this silliness? Is it react or a web component?!
// It's a web component we use to bootstrap react apps within the monolith.
@controller('react-app')
export class ReactAppElement extends ReactBaseElement<EmbeddedData> {
  nameAttribute = 'app-name'
  declare uuid: string
  declare routerOrHistory: RouterOrHistory
  appRoutes: NavigatorRouteRegistration[] = []
  #connectionState: PreparedConnectionState = {}

  get enabledFeatures() {
    if (!this.embeddedDataJSON) return []
    const features = this.embeddedDataJSON.appPayload?.enabled_features || {}
    return Object.keys(features).filter(key => features[key as keyof typeof features])
  }

  get routes() {
    if ((this.appRoutes ?? []).length > 0) {
      return this.appRoutes
    }

    return (this.routerOrHistory as RouterProviderProps['router'])?.routes ?? []
  }

  get navigate() {
    if ('push' in this.routerOrHistory) {
      return this.routerOrHistory.push
    }

    if ('navigate' in this.routerOrHistory) {
      return this.routerOrHistory.navigate
    }
  }

  override connectedCallback() {
    // Reset before `super.connectedCallback()`, which synchronously starts the component preload.
    this.#connectionState = {}
    super.connectedCallback()
    this.uuid = generateAppId()
    registerAppId(this.uuid)

    window.addEventListener('popstate', this.popStateListener, true)
  }

  popStateListener = (e: PopStateEvent) => {
    // not having a new state means it's a hash navigation
    if (e.state && this.uuid !== currentState().appId) this.#disposeRouter()
  }

  override disconnectedCallback() {
    window.removeEventListener('popstate', this.popStateListener, true)
    this.#disposeRouter()
    this.#connectionState = {}
    super.disconnectedCallback()
  }

  /**
   * Disposes the current router or history instance.
   * Handles different cleanup methods for different router types:
   * - React Router: dispose()
   * - Navigator history: dispose()
   * - TanStack Router: history.destroy()
   */
  #disposeRouter() {
    if (!this.routerOrHistory) return

    // TanStack Router uses history.destroy() for cleanup
    if (isTanStackRouter(this.routerOrHistory)) {
      this.routerOrHistory.history.destroy()
    } else if ('dispose' in this.routerOrHistory) {
      // React Router and Navigator history use dispose()
      this.routerOrHistory.dispose()
    }
  }

  // The component that wraps this React app will know if the app should be rendered with
  // date router (instead of navigator router) and it does this by setting
  // a `data-data-router-enabled="true"` attribute on this `<react-app>` element.
  // Remember we might have two apps called `some-cool-app`, because it started as a navigator app
  // (i.e. jsonRoute) and now also exists a data router app (i.e. queryRoute). At runtime, we might
  // toggle between which to use based on feature flags.
  get isDataRouterEnabled() {
    return this.getAttribute('data-data-router-enabled') === 'true'
  }

  get includeAppShell() {
    return this.getAttribute('data-app-shell') === 'true'
  }

  protected override startReactComponentPreload(
    embeddedData: Promise<EmbeddedData>,
  ): ReactComponentPreloadHandle | null {
    const state = this.#connectionState
    state.componentPreloadStarted = true
    if (this.isDataRouterEnabled) {
      return {
        completion: this.#preloadDataRouterComponents(state, embeddedData),
        onTimeout: () => this.#clearComponentPreloadState(state),
      }
    }

    return this.#startNavigatorComponentPreload(state)
  }

  #clearComponentPreloadState(state: PreparedConnectionState) {
    state.navigatorRegistration = undefined
    state.matchedNavigatorComponents = undefined
    state.preparedDataRouter = undefined
    state.matchedDataRouterComponents = undefined
  }

  #startNavigatorComponentPreload(state: PreparedConnectionState): ReactComponentPreloadHandle {
    const preloadStarted = Promise.withResolvers<void>()
    const completion = (async () => {
      try {
        const registration = await this.#getNavigatorRegistration(state)
        this.#preloadNavigatorComponents(state, registration.routes, this.#getInitialLocation())
        preloadStarted.resolve()
        await state.matchedNavigatorComponents
      } catch (error) {
        preloadStarted.reject(error)
        throw error
      }
    })()
    return {
      beforeDataParsing: preloadStarted.promise,
      completion,
      onTimeout: () => this.#clearComponentPreloadState(state),
    }
  }

  async getReactNode(embeddedData: EmbeddedData, onError: (error: Error) => void): Promise<React.ReactNode> {
    const state = this.#connectionState
    if (this.isDataRouterEnabled) {
      const preparedRouter = await this.#getPreparedDataRouterForRender(state, embeddedData)
      if (!preparedRouter || !this.#isCurrentConnection(state)) return null

      const node = await this.#getDataRouterNode(embeddedData, onError, preparedRouter)
      if (!state.componentPreloadStarted) {
        await this.#preloadDataRouterComponents(state, Promise.resolve(embeddedData))
      }
      return node
    }

    const registration = await this.#getNavigatorRegistration(state)
    if (!this.#isCurrentConnection(state)) return null

    return this.#getNavigatorNode(embeddedData, onError, registration, state)
  }

  async #getPreparedDataRouterForRender(state: PreparedConnectionState, embeddedData: EmbeddedData) {
    const preparation = this.#getPreparedDataRouter(state, embeddedData)
    try {
      return await preparation
    } catch (error) {
      if (!this.#isCurrentConnection(state)) return null
      if (!state.componentPreloadStarted) throw error

      if (state.preparedDataRouter === preparation) state.preparedDataRouter = undefined
      return this.#getPreparedDataRouter(state, embeddedData)
    }
  }

  #isCurrentConnection(state: PreparedConnectionState) {
    return state === this.#connectionState
  }

  #getNavigatorRegistration(state: PreparedConnectionState) {
    state.navigatorRegistration ??= clearCachedPromiseOnRejection(this.#loadNavigatorRegistration(), promise => {
      if (state.navigatorRegistration === promise) state.navigatorRegistration = undefined
    })
    return state.navigatorRegistration
  }

  #preloadNavigatorComponents(
    state: PreparedConnectionState,
    routes: NavigatorRouteRegistration[],
    location: Parameters<typeof preloadMatchedRouteComponents>[1],
  ) {
    state.matchedNavigatorComponents ??= clearCachedPromiseOnRejection(
      preloadMatchedRouteComponents(routes, location),
      promise => {
        if (state.matchedNavigatorComponents === promise) state.matchedNavigatorComponents = undefined
      },
    )
    return state.matchedNavigatorComponents
  }

  async #loadNavigatorRegistration() {
    const app = await getReactNavigatorApp(this.name)
    return app.registration()
  }

  #getPreparedDataRouter(state: PreparedConnectionState, embeddedData: EmbeddedData) {
    state.preparedDataRouter ??= clearCachedPromiseOnRejection(this.#prepareDataRouter(embeddedData), promise => {
      if (state.preparedDataRouter === promise) state.preparedDataRouter = undefined
    })
    return state.preparedDataRouter
  }

  async #prepareDataRouter(embeddedData: EmbeddedData): Promise<PreparedDataRouter> {
    const app = await getReactDataRouterApp(this.name)
    let {routes} = app.registration({embeddedData})
    if (this.includeAppShell) {
      routes = await wrapWithAppShell(routes, embeddedData)
    }

    return {routes, withTanStackRouter: isTanStackRouterEnabled(app.tanStackRouterEnabled)}
  }

  async #preloadDataRouterComponents(state: PreparedConnectionState, embeddedData: Promise<EmbeddedData>) {
    state.matchedDataRouterComponents ??= clearCachedPromiseOnRejection(
      (async () => {
        const preparedRouter = await this.#getPreparedDataRouter(state, await embeddedData)
        await preloadMatchedDataRouterComponents(preparedRouter.routes, this.#getInitialLocation().pathname)
      })(),
      promise => {
        if (state.matchedDataRouterComponents === promise) state.matchedDataRouterComponents = undefined
      },
    )
    await state.matchedDataRouterComponents
  }

  async #getDataRouterNode(
    embeddedData: EmbeddedData,
    onError: (error: Error) => void,
    {routes, withTanStackRouter}: PreparedDataRouter,
  ) {
    this.#extractRailsPartials(embeddedData)
    getQueryClient().removeQueries({queryKey: [this.name]})

    const routerProvider = withTanStackRouter
      ? await this.#createTanStackRouter(embeddedData, routes)
      : await this.#createReactRouter(embeddedData, routes)

    return (
      <ProfilerProvider appName={this.name} isDataRouterEnabled>
        <AppProfiler id={this.name}>{routerProvider}</AppProfiler>
      </ProfilerProvider>
    )
  }

  async #createReactRouter(embeddedData: EmbeddedData, routes: RouteObject[]) {
    this.routerOrHistory = wrapRouterNavigateForTurboAndSoftNavs(
      createBrowserRouter(
        routesWithProviders(routes, {
          appPayload: embeddedData.appPayload,
          ssrError: this.ssrError,
          appName: this.name,
          dataRouterEnabled: true,
        }),
      ),
    )

    return <RouterProvider router={this.routerOrHistory} />
  }

  async #createTanStackRouter(embeddedData: EmbeddedData, routes: RouteObject[]) {
    const queryClient = getQueryClient()
    const router = tanStackRouterWithProviders(routes, {
      appPayload: embeddedData.appPayload,
      ssrError: this.ssrError,
      appName: this.name,
      context: {embeddedData, queryClient},
    })
    this.routerOrHistory = router
    // Pre-resolve matched route data/code before first render to prevent suspense flash,
    // similar to preloadMatchedDataRouterComponents for React Router path.
    await router.load()
    if (this.hasSSRContent) {
      // SSR markup only needs the post-hydration router flag here.
      // router.load() has already populated the matched route data from embedded data,
      // so a truthy router.ssr is enough for the first client render to match the SSR output.
      router.ssr = {manifest: undefined}
    }
    return <TanStackRouterProvider router={router} />
  }

  async #getNavigatorNode(
    embeddedData: EmbeddedData,
    onError: (error: Error) => void,
    registration: NavigatorAppRegistration,
    state: PreparedConnectionState,
  ) {
    const {App, routes} = registration
    const initialLocation = this.#getInitialLocation()

    if (this.isLazy) {
      const initialPath = this.getAttribute('initial-path') as string
      const request = await fetch(initialPath, {
        mode: 'no-cors',
        cache: 'no-cache',
        credentials: 'include',
      })
      if (!this.#isCurrentConnection(state)) return null

      const {payload} = await request.json()
      if (!this.#isCurrentConnection(state)) return null

      embeddedData.payload = payload
    }

    if (!this.#isCurrentConnection(state)) return null

    // set the `key` property to avoid using `default` as the initial location's `key`.
    updateCurrentState({key: createKey()})

    const history = createBrowserHistory({window})
    this.routerOrHistory = history
    this.appRoutes = routes
    const locationWithHistoryState = {
      ...initialLocation,
      key: history.location.key,
      state: history.location.state,
    }

    // need to await/preload to avoid suspense flash on initial render
    const componentPreload = this.#preloadNavigatorComponents(state, routes, locationWithHistoryState)
    if (!state.componentPreloadStarted) await componentPreload

    return (
      <ProfilerProvider appName={this.name} isDataRouterEnabled={false}>
        <AppProfiler id={this.name}>
          <NavigatorClientEntry
            appName={this.name}
            initialLocation={locationWithHistoryState}
            history={history}
            embeddedData={embeddedData}
            routes={routes}
            App={App}
            ssrError={this.ssrError}
            onError={onError}
          />
        </AppProfiler>
      </ProfilerProvider>
    )
  }

  #getInitialLocation() {
    const initialPath = this.getAttribute('initial-path') as string
    const window = globalThis.window as Window | undefined

    // Initial path is set by ruby. Anchors are not sent to the server.
    // Therefore anchors must be set explicitly by the client.
    const {pathname, search, hash} = new URL(
      `${initialPath}${window?.location.hash ?? ''}`,
      window?.location.href ?? 'https://github.com',
    )

    return {pathname, search, hash, key: '', state: null}
  }

  #extractRailsPartials(embeddedData: EmbeddedData) {
    if (!embeddedData.payload) return

    const embeddedRailsPartials = this.querySelectorAll('rails-partial')

    for (const railsPartial of embeddedRailsPartials) {
      const name = railsPartial.getAttribute('data-partial-name')

      if (!name) continue

      const payload = embeddedData.payload as Record<string, unknown>

      payload[name] = create(SafeHtmlSchema, {value: railsPartial.innerHTML})
    }
  }

  get isLazy() {
    return this.getAttribute('data-lazy') === 'true'
  }
}
